import LinkButton from '../../../components/LinkButton';
import NotFound from '../../../components/NotFound';
import {
  VotingBadge,
  VotingBadges,
} from '../../../components/Votings/VotingBadges';
import VotingResultsView from '../../../components/Votings/VotingResultsView';
import { getVotingDetail } from '../../../features/queries/getVotingDetail';
import { getVotingResults } from '../../../features/queries/getVotingResults';
import { entityId } from '../../../lib/ids';
import { useQ } from '../../../lib/query';
import {
  citizenResultsVisible,
  citizenVotingStatus,
  votingDetailPath,
  votingResultsPublished,
  votingState,
} from '../../../lib/votings';
import { useLayout } from '../../../providers/LayoutProvider';
import {
  __,
  formatDateTime,
  getVotesDeclension,
  toastError,
} from '../../../utils';
import { useAuth } from '@presov.sk/presov-auth-react';
import { Card, Skeleton, Stack, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';
import { useParams } from 'react-router';

/**
 * Výsledky hlasovania pre občana.
 *
 * Vlastná adresa, nie tab detailu: odkazuje sem hlavička sústredeného režimu,
 * potvrdenie po odoslaní aj zoznam ukončených hlasovaní, a odkaz na výsledky
 * má viesť na výsledky - nie na stránku, ktorú si treba prepnúť.
 */
const VotingResultsPage: React.FC = () => {
  const { votingId: votingIdParam } = useParams() as { votingId: string };
  const votingId = entityId(votingIdParam, 'voting');
  const { setPageTitle, setBackPath } = useLayout();
  const { authState } = useAuth();
  const now = React.useMemo(() => new Date(), []);
  const isAuthorizing = authState === 'authorizing';

  // `enabled` je tu z rovnakého dôvodu ako v [[VotingsPage]]: kým `AuthProvider`
  // nedobehne, token na klientovi nie je a server by neverejné hlasovanie nevydal.
  const {
    data: detail,
    isLoading: isLoadingDetail,
    isError: isErrorDetail,
  } = useQ(getVotingDetail, { votingId }, { enabled: !isAuthorizing });

  const state = detail ? votingState(detail, now) : undefined;
  const status = detail ? citizenVotingStatus(detail, now) : undefined;

  // Kým hlasovanie beží a priebežné výsledky zapnuté nie sú - a rovnako, keď
  // výsledky nezverejňuje vôbec - server podrobnosti nevydá. Nemá zmysel sa
  // naň pýtať a dostať prázdne `questions`, z ktorých by bola biela plocha.
  const detailedAvailable = !!detail && citizenResultsVisible(detail, now);

  const {
    data: questions = [],
    isLoading: isLoadingResults,
    isError: isErrorResults,
  } = useQ(getVotingResults, { votingId }, { enabled: detailedAvailable });

  React.useEffect(() => {
    setPageTitle(detail?.title ?? __('Výsledky hlasovania'));
    setBackPath(votingDetailPath(votingId));
    return () => setBackPath(undefined);
  }, [detail?.title, setBackPath, setPageTitle, votingId]);

  React.useEffect(() => {
    if (isErrorResults) {
      toastError(__('Výsledky hlasovania sa nepodarilo načítať.'));
    }
  }, [isErrorResults]);

  if (isErrorDetail && !isAuthorizing) {
    return (
      <NotFound
        message={__('Hlasovanie sa nenašlo.')}
        backUrl='/hlasovania'
        backText={__('Späť na hlasovania')}
      />
    );
  }

  if (isLoadingDetail || isAuthorizing || !detail) {
    return (
      <Stack gap={1.5} sx={{ maxWidth: 760, mx: 'auto', width: '100%' }}>
        <Skeleton variant='rounded' height={26} width={320} />
        <Skeleton variant='rounded' height={220} />
      </Stack>
    );
  }

  return (
    <Stack gap={1.5} sx={{ maxWidth: 760, mx: 'auto', width: '100%' }}>
      <Stack direction='row' alignItems='center' gap={1} flexWrap='wrap'>
        <VotingBadges>
          {status && (
            <VotingBadge dot tone={state === 'open' ? 'info' : 'neutral'}>
              {detail.open_to
                ? `${status.label} · ${state === 'closed' ? __('skončilo') : __('do')} ${formatDateTime(detail.open_to)}`
                : status.label}
            </VotingBadge>
          )}
          <VotingBadge>{`${detail.votes_count} ${getVotesDeclension(detail.votes_count)}`}</VotingBadge>
        </VotingBadges>
        {state === 'open' && detailedAvailable && (
          <Typography variant='caption' color='text.secondary'>
            {__('Priebežný stav sa môže zmeniť do uzavretia hlasovania.')}
          </Typography>
        )}
      </Stack>

      {!detailedAvailable ? (
        <Card sx={{ p: 3, gap: 1.25, alignItems: 'flex-start' }}>
          {/* Dve rôzne správy, nie jedna opatrná: „zatiaľ" a „vôbec" sú pre
              toho, kto hlasoval, celkom iné odpovede. */}
          <Typography component='b' sx={{ fontSize: 17, fontWeight: 700 }}>
            {votingResultsPublished(detail)
              ? __('Podrobné výsledky sa zverejnia po uzavretí')
              : __('Podrobné výsledky tohto hlasovania sa nezverejňujú')}
          </Typography>
          <Typography variant='body1' color='text.secondary'>
            {votingResultsPublished(detail)
              ? `${__('Počas hlasovania je viditeľný len počet hlasov - zatiaľ ich je')} ${detail.votes_count}.`
              : `${__('Zverejnený je len počet hlasov - je ich')} ${detail.votes_count}.`}
          </Typography>
          <LinkButton variant='outline' to={votingDetailPath(votingId)}>
            {__('Späť na hlasovanie')}
          </LinkButton>
        </Card>
      ) : isLoadingResults ? (
        <>
          <Skeleton variant='rounded' height={220} />
          <Skeleton variant='rounded' height={220} />
        </>
      ) : (
        <VotingResultsView
          questions={questions}
          votesCount={detail.votes_count}
          tone='citizen'
          myVotes={detail.my_votes}
        />
      )}
    </Stack>
  );
};

export default VotingResultsPage;
